import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';

function AgriMarketing({ filters, loading, setLoading, isStaging, onDrilldown }) {
  const [brandHealth, setBrandHealth] = useState({ labels: [], volume: [], health: [] });
  const [topicVolume, setTopicVolume] = useState({ labels: [], datasets: [] });
  const [brandKeywords, setBrandKeywords] = useState([]);
  const [marketShareTrend, setMarketShareTrend] = useState({ labels: [], datasets: [] });
  const [landscape, setLandscape] = useState([]);
  const [sentimentByComp, setSentimentByComp] = useState({ labels: [], datasets: [] });
  const [brandCrop, setBrandCrop] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });

    Promise.all([
      axios.get(`/api/agri/marketing/brand-health-trend?${params}`),
      axios.get(`/api/agri/marketing/conv-volume-by-topic?${params}`),
      axios.get(`/api/agri/marketing/brand-keywords?${params}`),
      axios.get(`/api/agri/marketing/market-share-trend?${params}`),
      axios.get(`/api/agri/marketing/competitive-landscape?${params}`),
      axios.get(`/api/agri/marketing/sentiment-by-competitor?${params}`),
      axios.get(`/api/agri/marketing/brand-crop-association?${params}`),
    ])
      .then(([bhRes, tvRes, bkRes, msRes, clRes, scRes, bcRes]) => {
        setBrandHealth(bhRes.data);
        setTopicVolume(tvRes.data);
        setBrandKeywords(bkRes.data);
        setMarketShareTrend(msRes.data);
        setLandscape(clRes.data);
        setSentimentByComp(scRes.data);
        setBrandCrop(bcRes.data);
      })
      .catch(err => console.error('Error loading Agri Marketing:', err))
      .finally(() => setLoading(false));
  }, [filters, setLoading]);

  const cardStyle = {
    background: '#fff', borderRadius: 8, padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };
  const gridStyle = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: 16,
  };
  const plotLayout = { margin: { t: 30, b: 40, l: 50, r: 20 }, height: 300, paper_bgcolor: 'transparent', plot_bgcolor: 'transparent' };
  const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];

  return (
    <div style={gridStyle}>
      {/* Brand Health Trend */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Brand Health Trend</h3>
        <Plot
          data={[
            { x: brandHealth.labels, y: brandHealth.volume, type: 'bar', name: 'Volume', marker: { color: '#3498db' } },
            { x: brandHealth.labels, y: brandHealth.health, type: 'scatter', mode: 'lines', name: 'Health', yaxis: 'y2', line: { color: '#e74c3c' } },
          ]}
          layout={{ ...plotLayout, yaxis: { title: 'Volume' }, yaxis2: { title: 'Health', overlaying: 'y', side: 'right', range: [0, 100] } }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Conversation Volume by Topic */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Conversation Volume by Topic</h3>
        <Plot
          data={(topicVolume.datasets || []).map((ds, i) => ({
            x: topicVolume.labels,
            y: ds.data,
            type: 'bar',
            name: ds.label,
            marker: { color: colors[i % colors.length] },
          }))}
          layout={{ ...plotLayout, barmode: 'stack' }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Brand Keywords */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Brand Keywords</h3>
        {brandKeywords.length > 0 ? (
          <Plot
            data={[{
              x: brandKeywords.map(k => k.size),
              y: brandKeywords.map(k => k.text),
              type: 'bar',
              orientation: 'h',
              marker: { color: '#9b59b6' },
            }]}
            layout={{ ...plotLayout, height: Math.max(300, brandKeywords.length * 25), yaxis: { automargin: true } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No keyword data</div>
        )}
      </div>

      {/* Market Share Trend */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Market Share Trend</h3>
        <Plot
          data={(marketShareTrend.datasets || []).map((ds, i) => ({
            x: marketShareTrend.labels,
            y: ds.data,
            type: 'scatter',
            mode: 'lines+markers',
            name: ds.label,
            line: { color: colors[i % colors.length] },
          }))}
          layout={plotLayout}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Competitive Landscape Bubble */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Competitive Landscape</h3>
        {landscape.length > 0 ? (
          <Plot
            data={[{
              x: landscape.map(l => l.x),
              y: landscape.map(l => l.y),
              text: landscape.map(l => l.company_name),
              mode: 'markers+text',
              type: 'scatter',
              textposition: 'top center',
              marker: {
                size: landscape.map(l => Math.max(20, Math.sqrt(l.r) * 5)),
                color: landscape.map((_, i) => colors[i % colors.length]),
                opacity: 0.7,
              },
            }]}
            layout={{ ...plotLayout, xaxis: { title: 'Mentions' }, yaxis: { title: '' } }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No data</div>
        )}
      </div>

      {/* Sentiment by Competitor */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Sentiment by Competitor</h3>
        <Plot
          data={(sentimentByComp.datasets || []).map((ds, i) => ({
            x: sentimentByComp.labels,
            y: ds.data,
            type: 'scatter',
            mode: 'lines+markers',
            name: ds.label,
            line: { color: colors[i % colors.length] },
          }))}
          layout={{ ...plotLayout, yaxis: { title: 'Sentiment', range: [0, 100] } }}
          config={{ responsive: true, displayModeBar: false }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Brand-Crop Association Sunburst */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Brand-Crop Association</h3>
        {brandCrop.length > 0 ? (
          <Plot
            data={[{
              type: 'sunburst',
              labels: ['All', ...new Set(brandCrop.map(r => r.parent)), ...brandCrop.map(r => r.label)],
              parents: ['', ...new Set(brandCrop.map(r => r.parent)).values().map(() => 'All'), ...brandCrop.map(r => r.parent)],
              values: [0, ...Array.from(new Set(brandCrop.map(r => r.parent))).map(p =>
                brandCrop.filter(r => r.parent === p).reduce((s, r) => s + r.value, 0)
              ), ...brandCrop.map(r => r.value)],
              branchvalues: 'total',
            }]}
            layout={{ ...plotLayout, height: 350 }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%' }}
          />
        ) : (
          <div style={{ color: '#999', padding: 40, textAlign: 'center' }}>No association data</div>
        )}
      </div>
    </div>
  );
}

export default AgriMarketing;
